import { jsPDF } from "jspdf";
import moment from "moment";

const API_BASE = "https://api.fixupnow.in";

// ─── helpers ────────────────────────────────────────────────────────────────

function numberToWords(num) {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if (num === 0) return "Zero";
  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
    return "";
  };
  const lakhs = Math.floor(num / 100000);
  const thousands = Math.floor((num % 100000) / 1000);
  const remainder = num % 1000;
  let result = "";
  if (lakhs) result += convert(lakhs) + " Lakh ";
  if (thousands) result += convert(thousands) + " Thousand ";
  if (remainder) result += convert(remainder);
  return result.trim();
}

/**
 * Fetches an image via the backend proxy (bypasses all CORS issues).
 * whiteBackground=true paints white under the image (for transparent logos).
 * whiteBackground=false keeps the raw data (for signatures — ink on transparent).
 */
async function loadImageViaProxy(url, whiteBackground = false) {
  if (!url) return null;
  try {
    const token = localStorage.getItem("authToken");
    const res = await fetch(
      `${API_BASE}/utils/image-proxy?url=${encodeURIComponent(url)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) return null;
    const json = await res.json();
    const base64 = json?.data?.base64;
    if (!base64) return null;

    if (!whiteBackground) return base64; // signature: use as-is

    // For logo: paint white bg so transparent areas become white
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width  = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => resolve(base64);
      img.src = base64;
    });
  } catch (_) {
    return null;
  }
}

// ─── main export ────────────────────────────────────────────────────────────

export async function generateManagerInvoice(manager) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  const PW = doc.internal.pageSize.getWidth();   // 595
  const PH = doc.internal.pageSize.getHeight();  // 842
  const M  = 40;   // margin
  const CW = PW - 2 * M;  // content width

  const invoiceNumber = `INV-${manager.uniqueId || manager.userId?.slice(-6).toUpperCase()}-${Date.now().toString().slice(-6)}`;
  const currentDate   = moment().format("DD/MM/YYYY");
  const paymentDate   = manager.registrationPaidAt
    ? moment(manager.registrationPaidAt).format("DD/MM/YYYY, hh:mm A")
    : currentDate;
  const contractEnd   = manager.registrationExpiresAt
    ? moment(manager.registrationExpiresAt).format("DD/MM/YYYY")
    : "N/A";
  const feeAmount     = manager.registrationFee || 15000;
  const registrationId = `MGR-${manager.uniqueId || manager.userId?.slice(-6).toUpperCase()}`;
  const transactionId  = manager.paymentTransactionId || "N/A";

  // ── HEADER BAND ────────────────────────────────────────────────────────────
  const HDR = 110;

  // Navy base
  doc.setFillColor(26, 35, 126);
  doc.rect(0, 0, PW, HDR, "F");

  // Green top strip
  doc.setFillColor(139, 195, 74);
  doc.rect(0, 0, PW, 5, "F");

  // Green bottom strip
  doc.setFillColor(139, 195, 74);
  doc.rect(0, HDR - 5, PW, 5, "F");

  // Dark accent triangle top-right
  doc.setFillColor(13, 23, 87);
  doc.triangle(PW - 80, 5, PW, 5, PW, 60, "F");

  // Logo — load with white canvas background then embed
  const logoUrl  = "https://fixupnow.in/fixupnow.png?sdf=32";
  const logoData = await loadImageViaProxy(logoUrl, false); // white bg for transparent logo PNG
  const logoSize = 75;
  const logoX    = M;
  const logoY    = (HDR - logoSize) / 2;

  if (logoData) {
    // White circle behind logo
    doc.setFillColor(255, 255, 255);
    doc.circle(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2 + 10, "F");
    doc.addImage(logoData, "PNG", logoX, logoY, logoSize, logoSize);
  }

  // Brand text
  const bX = logoX + logoSize + 20;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(30);
  doc.setFont("helvetica", "bold");
  doc.text("FIXUPNOW", bX, 44);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(197, 202, 233);
  doc.text("Manager Registration Invoice", bX, 62);

  doc.setFontSize(8);
  doc.setTextColor(159, 168, 218);
  doc.text("nowfixup@gmail.com  |  18003094266  |  fixupnow.in", bX, 78);

  let y = HDR + 22;

  // ── INVOICE TITLE + META BOX ───────────────────────────────────────────────
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(26, 35, 126);
  doc.text("INVOICE", M, y + 20);

  // Meta box
  const mbW = 190, mbH = 62, mbX = PW - M - mbW;
  doc.setFillColor(232, 234, 246);
  doc.roundedRect(mbX, y, mbW, mbH, 6, 6, "F");

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(136, 136, 136);
  doc.text("Invoice No.", mbX + 10, y + 14);

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(26, 35, 126);
  doc.text(invoiceNumber, mbX + 10, y + 28);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(136, 136, 136);
  doc.text("Invoice Date", mbX + 10, y + 44);

  doc.setFontSize(9);
  doc.setTextColor(51, 51, 51);
  doc.text(currentDate, mbX + 10, y + 56);

  y += 76;

  // Divider
  doc.setDrawColor(197, 202, 233);
  doc.setLineWidth(0.8);
  doc.line(M, y, PW - M, y);
  y += 16;

  // ── BILL TO + CONTRACT DETAILS ─────────────────────────────────────────────
  const colW  = (CW - 20) / 2;
  const col2X = M + colW + 20;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(26, 35, 126);
  doc.text("Bill To", M, y);
  doc.text("Contract Details", col2X, y);
  y += 14;

  const billLines = [
    manager.name || "N/A",
    manager.mobileNumber || "N/A",
    manager.email || "N/A",
    manager.fullAddress || "N/A",
    `${manager.city || "N/A"}  —  ${manager.pincode || "N/A"}`,
  ];
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(51, 51, 51);

  let leftY = y, rightY = y;
  billLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, colW);
    doc.text(wrapped, M, leftY);
    leftY += wrapped.length * 13;
  });

  const contractLines = [
    `Period: ${paymentDate}`,
    `Expires: ${contractEnd}`,
    "Duration: 1 Year",
    `Registration ID: ${registrationId}`,
  ];
  contractLines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, colW);
    doc.text(wrapped, col2X, rightY);
    rightY += wrapped.length * 13;
  });

  y = Math.max(leftY, rightY) + 18;

  // ── ITEMS TABLE ────────────────────────────────────────────────────────────
  const tC1 = M, tC2 = M + 24, tC4 = PW - M - 90, tCW = 90;
  const tC3 = tC4 - 80, descW = tC3 - tC2 - 8;

  // Header row
  doc.setFillColor(26, 35, 126);
  doc.rect(M, y - 5, CW, 22, "F");
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("#", tC1, y + 9);
  doc.text("Description", tC2, y + 9);
  doc.text("HSN/SAC", tC3, y + 9);
  doc.text("Amount (Rs.)", PW - M, y + 9, { align: "right" });
  y += 26;

  // Data row
  doc.setFillColor(245, 245, 245);
  doc.rect(M, y - 4, CW, 24, "F");
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(51, 51, 51);
  doc.text("1", tC1, y + 9);
  doc.text("Manager Registration Fee", tC2, y + 9);
  doc.text("9985", tC3, y + 9);
  doc.text(`Rs. ${feeAmount.toLocaleString("en-IN")}`, PW - M, y + 9, { align: "right" });
  y += 32;

  // Sub total / Tax / Total
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(85, 85, 85);
  doc.text("Sub Total:", tC3 - 10, y, { align: "right" });
  doc.text(`Rs. ${feeAmount.toLocaleString("en-IN")}`, PW - M, y, { align: "right" });
  y += 14;
  doc.text("Tax (0%):", tC3 - 10, y, { align: "right" });
  doc.text("Rs. 0", PW - M, y, { align: "right" });
  y += 10;

  doc.setDrawColor(170, 170, 170);
  doc.setLineWidth(0.4);
  doc.line(tC3 - 90, y, PW - M, y);
  y += 8;

  // Total row
  doc.setFillColor(26, 35, 126);
  doc.rect(tC3 - 95, y - 5, PW - M - (tC3 - 95), 24, "F");
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("TOTAL", tC3 - 10, y + 11, { align: "right" });
  doc.text(`Rs. ${feeAmount.toLocaleString("en-IN")}`, PW - M, y + 11, { align: "right" });
  y += 32;

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(85, 85, 85);
  doc.text(`Amount in words: Rupees ${numberToWords(feeAmount)} Only/-`, M, y);
  y += 20;

  // ── PAYMENT DETAILS + TERMS ────────────────────────────────────────────────
  doc.setDrawColor(197, 202, 233);
  doc.setLineWidth(0.8);
  doc.line(M, y, PW - M, y);
  y += 16;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(26, 35, 126);
  doc.text("Payment Details", M, y);
  doc.text("Terms & Conditions", col2X, y);
  y += 14;

  const payRows = [
    ["Payment Date",   paymentDate],
    ["Payment Mode",   "Online Payment"],
    ["Transaction ID", transactionId],
    ["Valid Until",    contractEnd],
    ["Amount Paid",    `Rs. ${feeAmount.toLocaleString("en-IN")}`],
  ];
  let pyY = y, tyY = y;

  payRows.forEach(([label, val]) => {
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 51, 51);
    doc.text(`${label}:`, M, pyY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(85, 85, 85);
    const wrapped = doc.splitTextToSize(val, colW - 90);
    doc.text(wrapped, M + 95, pyY);
    pyY += Math.max(wrapped.length * 13, 15);
  });

  const terms = [
    "1. Registration fee is non-refundable.",
    "2. Contract valid for 1 year from payment date.",
    "3. This is a computer-generated invoice.",
    "4. Disputes subject to Shahabad, Haryana jurisdiction.",
    "5. Does not require a physical signature.",
  ];
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(85, 85, 85);
  terms.forEach((t) => {
    const wrapped = doc.splitTextToSize(t, colW);
    doc.text(wrapped, col2X, tyY);
    tyY += wrapped.length * 13 + 3;
  });

  y = Math.max(pyY, tyY) + 20;

  // ── SIGNATURE ─────────────────────────────────────────────────────────────
  const footerH = 30, sigBlockH = 100;
  const sigZoneY = PH - footerH - sigBlockH - 10;
  if (y > sigZoneY - 10) { doc.addPage(); y = 40; }

  const pinnedY = PH - footerH - sigBlockH - 10;
  doc.setDrawColor(197, 202, 233);
  doc.setLineWidth(0.8);
  doc.line(M, pinnedY, PW - M, pinnedY);

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(51, 51, 51);
  doc.text("Client Signature", PW / 2, pinnedY + 16, { align: "center" });

  if (manager.signatureUrl) {
    try {
      const sigData = await loadImageViaProxy(manager.signatureUrl, false); // keep transparent bg for signature ink
      if (sigData) {
        doc.addImage(sigData, "PNG", PW / 2 - 60, pinnedY + 22, 120, 50);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(85, 85, 85);
        const acceptedOn = manager.termsAcceptedAt
          ? moment(manager.termsAcceptedAt).format("DD/MM/YYYY, hh:mm A")
          : currentDate;
        doc.text(`Accepted: ${acceptedOn}`, PW / 2, pinnedY + 80, { align: "center" });
      }
    } catch (_) {}
  }

  // ── FOOTER BAND ────────────────────────────────────────────────────────────
  doc.setFillColor(26, 35, 126);
  doc.rect(0, PH - footerH, PW, footerH, "F");
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(197, 202, 233);
  doc.text(
    "Thank you for choosing Fixupnow!  •  nowfixup@gmail.com  •  18003094266  •  fixupnow.in",
    PW / 2, PH - footerH + 12, { align: "center" }
  );

  // ── PAGE 2: RULES ──────────────────────────────────────────────────────────
  doc.addPage();

  doc.setFillColor(26, 35, 126);
  doc.rect(0, 0, PW, 55, "F");
  doc.setFillColor(139, 195, 74);
  doc.rect(0, 0, PW, 5, "F");

  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("Area Manager — Rules & Conditions", PW / 2, 35, { align: "center" });

  let ry = 75;
  const acceptedOnP2 = manager.termsAcceptedAt
    ? moment(manager.termsAcceptedAt).format("DD/MM/YYYY, hh:mm A")
    : currentDate;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(85, 85, 85);
  doc.text(`Accepted by ${manager.name || manager.mobileNumber || "N/A"} on ${acceptedOnP2}`, PW / 2, ry, { align: "center" });
  ry += 18;

  doc.setDrawColor(26, 35, 126);
  doc.setLineWidth(0.8);
  doc.line(M, ry, PW - M, ry);
  ry += 14;

  const rules = [
    { title: "Rule 1: Registration Fee Mandatory", content: "Payment of registration fee is mandatory for Area Manager. Only then you will be given a manager dashboard. This fee is non-refundable." },
    { title: "Rule 2: Partner Addition Timeline",  content: "If you fail to add 300 new partners within the first 2 months and 1500 partners within 1 year, the company may suspend you at any time." },
    { title: "Rule 3: Advertisement Condition",    content: "Only after you add 300 new partners in your area will the company run advertisements for you." },
    { title: "Rule 4: Partner Supervision",        content: "You will be responsible for overseeing all partners in your area and ensuring they pay the company commission." },
    { title: "Rule 5: Commission Eligibility",     content: "You receive commission only when the partner pays the company commission. If a partner does not pay, you will not receive commission for that work." },
    { title: "Rule 6: Site Visit Mandatory",       content: "Whenever the company instructs you regarding work in your area, you must visit the site." },
    { title: "Rule 7: Non-Compliance Consequences",content: "If you fail to follow any of these rules, the company may close your profile at any time." },
    { title: "Rule 8: Performance-Based Earnings", content: "Your earnings will depend on performance. There is no guaranteed income." },
  ];

  rules.forEach((rule) => {
    doc.setFillColor(232, 234, 246);
    doc.rect(M, ry - 2, CW, 18, "F");
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(211, 47, 47);
    doc.text(rule.title, M + 6, ry + 11);
    ry += 22;

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(68, 68, 68);
    const wrapped = doc.splitTextToSize(rule.content, CW - 12);
    doc.text(wrapped, M + 6, ry);
    ry += wrapped.length * 13 + 8;
  });

  ry += 6;
  doc.setDrawColor(221, 221, 221);
  doc.line(M, ry, PW - M, ry);
  ry += 12;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(153, 153, 153);
  doc.text("This document was generated automatically. The original agreement was accepted digitally.", PW / 2, ry, { align: "center" });
  doc.text(`Manager Signature: ${manager.signatureUrl ? "On File (Digital)" : "On File"}`, PW / 2, ry + 14, { align: "center" });

  // ── SAVE ───────────────────────────────────────────────────────────────────
  const fileName = `Invoice-${registrationId}-${moment().format("DDMMYYYY")}.pdf`;
  doc.save(fileName);
}
